import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import NColumnTable from "@/charts/NColumnTable";
import useGetQuery from "@/hooks/useGetQuery";
import { useQueryClient } from "@tanstack/react-query";
import { showApiError, showSuccess } from "@/utils";

export default function AEQueries() {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const queryClient = useQueryClient();
  const reviewUrl = `${BACKEND_URL}/api/v1/answer-engine-analytics/queries-for-review`;

  const [queriesResponse, queriesLoaded, queriesError] = useGetQuery(
    reviewUrl,
    { staleTime: 0 },
  );
  const [selectedQueries, setSelectedQueries] = useState(new Set());
  const [querySearchTerm, setQuerySearchTerm] = useState("");
  const [actionInProgress, setActionInProgress] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const handleSelectQuery = useCallback((id) => {
    setSelectedQueries((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleApprove = useCallback(
    async (item) => {
      setActionInProgress(`approve-${item.generated_query_id}`);
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/v1/answer-engine-analytics/queries-for-review/approve`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              file_id: item.file_id,
              generated_query_id: item.generated_query_id,
            }),
          },
        );
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.success) {
          showSuccess(
            t("answerEngine.queryApprovedSuccess", "Query approved and added to prompts"),
          );
          queryClient.invalidateQueries({ queryKey: [reviewUrl] });
        } else {
          showApiError(
            t("answerEngine.queryApproveError", "Failed to approve query. Please try again."),
          );
        }
      } catch {
        showApiError(
          t("answerEngine.queryApproveError", "Failed to approve query. Please try again."),
        );
      } finally {
        setActionInProgress(null);
      }
    },
    [BACKEND_URL, reviewUrl, queryClient, t],
  );

  const handleDiscard = useCallback(
    async (item) => {
      setActionInProgress(`discard-${item.generated_query_id}`);
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/v1/answer-engine-analytics/queries-for-review/discard`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              file_id: item.file_id,
              generated_query_id: item.generated_query_id,
            }),
          },
        );
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.success) {
          showSuccess(
            t("answerEngine.queryDiscardedSuccess", "Query discarded"),
          );
          setSelectedQueries((prev) => {
            const next = new Set(prev);
            next.delete(item.generated_query_id);
            return next;
          });
          queryClient.invalidateQueries({ queryKey: [reviewUrl] });
        } else {
          showApiError(
            t("answerEngine.queryDiscardError", "Failed to discard query. Please try again."),
          );
        }
      } catch {
        showApiError(
          t("answerEngine.queryDiscardError", "Failed to discard query. Please try again."),
        );
      } finally {
        setActionInProgress(null);
      }
    },
    [BACKEND_URL, reviewUrl, queryClient, t],
  );

  const getSelectedItems = () => {
    const allQueries = queriesResponse?.data || [];
    return allQueries.filter((q) => selectedQueries.has(q.generated_query_id));
  };

  const groupIdsByFileId = (items) => {
    const map = new Map();
    items.forEach((item) => {
      if (!map.has(item.file_id)) map.set(item.file_id, []);
      map.get(item.file_id).push(item.generated_query_id);
    });
    return map;
  };

  const handleBulkApprove = async () => {
    const items = getSelectedItems();
    if (!items.length) return;
    setIsBulkApproving(true);
    try {
      const groups = groupIdsByFileId(items);
      const responses = await Promise.all(
        Array.from(groups.entries()).map(([file_id, generated_query_ids]) =>
          fetch(
            `${BACKEND_URL}/api/v1/answer-engine-analytics/queries-for-review/approve-batch`,
            {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ file_id, generated_query_ids }),
            },
          ).then((res) => {
            if (!res.ok) throw new Error();
            return res.json();
          }),
        ),
      );
      const allResults = responses.flatMap((data) => data.results);
      const allSucceeded = allResults.every((r) => r.success);
      if (allSucceeded) {
        showSuccess(
          t("answerEngine.bulkApproveSuccess", {
            count: items.length,
            defaultValue: `${items.length} queries approved`,
          }),
        );
        setSelectedQueries(new Set());
      } else {
        showApiError(
          t(
            "answerEngine.bulkApproveError",
            "Some queries could not be approved. Please try again.",
          ),
        );
      }
      queryClient.invalidateQueries({ queryKey: [reviewUrl] });
    } catch {
      showApiError(
        t(
          "answerEngine.bulkApproveError",
          "Failed to approve selected queries. Please try again.",
        ),
      );
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleBulkDiscard = async () => {
    const items = getSelectedItems();
    if (!items.length) return;
    setIsBulkDeleting(true);
    try {
      const groups = groupIdsByFileId(items);
      const responses = await Promise.all(
        Array.from(groups.entries()).map(([file_id, generated_query_ids]) =>
          fetch(
            `${BACKEND_URL}/api/v1/answer-engine-analytics/queries-for-review/discard-batch`,
            {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ file_id, generated_query_ids }),
            },
          ).then((res) => {
            if (!res.ok) throw new Error();
            return res.json();
          }),
        ),
      );
      const allResults = responses.flatMap((data) => data.results);
      const allSucceeded = allResults.every((r) => r.success);
      if (allSucceeded) {
        showSuccess(
          t("answerEngine.bulkDiscardSuccess", {
            count: items.length,
            defaultValue: `${items.length} queries discarded`,
          }),
        );
        setSelectedQueries(new Set());
      } else {
        showApiError(
          t(
            "answerEngine.bulkDiscardError",
            "Some queries could not be discarded. Please try again.",
          ),
        );
      }
      queryClient.invalidateQueries({ queryKey: [reviewUrl] });
    } catch {
      showApiError(
        t(
          "answerEngine.bulkDiscardError",
          "Failed to discard selected queries. Please try again.",
        ),
      );
    } finally {
      setIsBulkDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const filteredBaseQueries = useMemo(() => {
    const allQueries = queriesResponse?.data || [];
    const s = querySearchTerm.trim().toLowerCase();

    if (!s) return allQueries;

    return allQueries.filter(
      (q) =>
        (q.query || "").toLowerCase().includes(s) ||
        (q.funnel_stage || "").toLowerCase().includes(s) ||
        (q.query_type || "").toLowerCase().includes(s) ||
        (q.is_branded === true
          ? "branded"
          : q.is_branded === false
            ? "non-branded"
            : ""
        ).includes(s),
    );
  }, [queriesResponse, querySearchTerm]);

  const allFilteredIds = useMemo(
    () => filteredBaseQueries.map((q) => q.generated_query_id),
    [filteredBaseQueries],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedQueries((prev) => {
      const allSelected =
        allFilteredIds.length > 0 && allFilteredIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        allFilteredIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...allFilteredIds]);
    });
  }, [allFilteredIds]);

  const filteredQueries = useMemo(() => {
    return filteredBaseQueries.map((q) => ({
      ...q,
      query: <span>{q.query}</span>,
      selectButton: (
        <input
          type="checkbox"
          checked={selectedQueries.has(q.generated_query_id)}
          onChange={() => handleSelectQuery(q.generated_query_id)}
          className="form-check-input"
          style={{ cursor: "pointer" }}
        />
      ),
      is_branded:
        q.is_branded === true ? (
          <span
            className="badge"
            style={{
              backgroundColor: "var(--badge-branded-bg)",
              color: "var(--badge-branded-text)",
            }}
          >
            {t("promptLibrary.branded", "Branded")}
          </span>
        ) : q.is_branded === false ? (
          <span
            className="badge"
            style={{
              backgroundColor: "var(--badge-unbranded-bg)",
              color: "var(--badge-unbranded-text)",
            }}
          >
            {t("promptLibrary.nonBranded", "Non-Branded")}
          </span>
        ) : (
          "-"
        ),
      funnel_stage: (() => {
        const stage = q.funnel_stage;
        if (!stage) return "-";
        const stageColors = {
          top: { bg: "var(--funnel-top-bg)", color: "var(--funnel-top-text)" },
          middle: {
            bg: "var(--funnel-mid-bg)",
            color: "var(--funnel-mid-text)",
          },
          bottom: {
            bg: "var(--funnel-bottom-bg)",
            color: "var(--funnel-bottom-text)",
          },
        };
        const style = stageColors[stage.toLowerCase()] || {
          bg: "var(--action-primary)",
          color: "white",
        };
        const stageLabel =
          stage.toLowerCase() === "top"
            ? t("tracking.topFunnel", "Top")
            : stage.toLowerCase() === "middle"
              ? t("tracking.middleFunnel", "Middle")
              : stage.toLowerCase() === "bottom"
                ? t("tracking.bottomFunnel", "Bottom")
                : stage;
        return (
          <span
            className="badge"
            style={{ backgroundColor: style.bg, color: style.color }}
          >
            {stageLabel}
          </span>
        );
      })(),
      query_type: (() => {
        const qt = q.query_type;
        if (!qt) return "-";
        const qtConfig = {
          ai_summary: {
            bg: "var(--badge-real-bg)",
            color: "var(--badge-real-text)",
            label: t("promptLibrary.real", "Real"),
          },
          real: {
            bg: "var(--badge-real-bg)",
            color: "var(--badge-real-text)",
            label: t("promptLibrary.real", "Real"),
          },
          synthetic: {
            bg: "var(--badge-synthetic-bg)",
            color: "var(--badge-synthetic-text)",
            label: t("promptLibrary.synthetic", "Synthetic"),
          },
        };
        const config = qtConfig[qt] || {
          bg: "var(--badge-unbranded-bg)",
          color: "var(--badge-unbranded-text)",
          label: qt,
        };
        return (
          <span
            className="badge"
            style={{ backgroundColor: config.bg, color: config.color }}
          >
            {config.label}
          </span>
        );
      })(),
      actions: (
        <div className="d-flex gap-2 justify-content-center">
          <button
            className="btn btn-sm btn-outline"
            onClick={() => handleApprove(q)}
            disabled={!!actionInProgress}
            title={t("common.approve", "Add to prompts")}
            style={{
              color: "var(--action-primary)",
              borderColor: "var(--action-primary)",
            }}
          >
            {actionInProgress === `approve-${q.generated_query_id}` ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <i className="bi bi-check-lg" />
            )}
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDiscard(q)}
            disabled={!!actionInProgress}
            title={t("common.discard", "Discard")}
          >
            {actionInProgress === `discard-${q.generated_query_id}` ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <i className="bi bi-trash" />
            )}
          </button>
        </div>
      ),
    }));
  }, [
    filteredBaseQueries,
    selectedQueries,
    handleSelectQuery,
    handleApprove,
    handleDiscard,
    actionInProgress,
    t,
  ]);

  const filteredExportData = useMemo(() => {
    const queriesToExport =
      selectedQueries.size > 0
        ? filteredBaseQueries.filter((q) =>
            selectedQueries.has(q.generated_query_id),
          )
        : filteredBaseQueries;

    return queriesToExport.map((q) => ({
      query: q.query || "",
      funnel_stage: q.funnel_stage || "-",
      is_branded:
        q.is_branded === true
          ? "Branded"
          : q.is_branded === false
            ? "Non-Branded"
            : "-",
    }));
  }, [filteredBaseQueries, selectedQueries]);

  return (
    <>
      <div className="mb-3 mt-1 d-flex align-items-center justify-content-between gap-2">
        <input
          type="text"
          className="form-control"
          placeholder={t("answerEngine.searchQueries", "Search queries...")}
          value={querySearchTerm}
          onChange={(e) => setQuerySearchTerm(e.target.value)}
          style={{ maxWidth: 400 }}
        />

        {selectedQueries.size > 0 && (
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm"
              onClick={handleBulkApprove}
              disabled={isBulkApproving || isBulkDeleting || !!actionInProgress}
              style={{
                backgroundColor: "var(--action-primary)",
                color: "white",
                borderColor: "var(--action-primary)",
              }}
            >
              {isBulkApproving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  {t("common.adding", "Adding...")}
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-1" />
                  {t("common.add", "Add")} ({selectedQueries.size})
                </>
              )}
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isBulkApproving || isBulkDeleting || !!actionInProgress}
            >
              <i className="bi bi-trash me-1" />
              {t("common.discard", "Discard")} ({selectedQueries.size})
            </button>
          </div>
        )}
      </div>

      <NColumnTable
        data={filteredQueries}
        exportData={filteredExportData}
        title={t("answerEngine.queriesReview", "AE Queries for Review")}
        isLoaded={queriesLoaded}
        error={queriesError}
        n={5}
        columnKeys={[
          "selectButton",
          "query",
          "funnel_stage",
          "is_branded",
          "actions",
        ]}
        columnTitles={[
          <input
            key="select-all"
            type="checkbox"
            className="form-check-input"
            style={{ cursor: "pointer" }}
            checked={
              allFilteredIds.length > 0 &&
              allFilteredIds.every((id) => selectedQueries.has(id))
            }
            onChange={handleSelectAll}
            aria-label={t("common.selectAll", "Select All")}
            title={t("common.selectAll", "Select All")}
          />,
          t("dashboard.query", "Query"),
          t("promptLibrary.funnelStage", "Funnel Stage"),
          t("promptLibrary.isBranded", "Branded"),
          t("common.actions", "Actions"),
        ]}
        columnRatio="3-50-15-15-17"
        columnAlignment="c-l-c-c-c"
        wrapText={true}
      />

      {showDeleteConfirm && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("answerEngine.confirmDiscard", "Confirm Discard")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteConfirm(false)}
                  aria-label={t("common.close", "Close")}
                />
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  {t("answerEngine.discardQueriesPrompt", {
                    count: selectedQueries.size,
                    item: selectedQueries.size > 1 ? "queries" : "query",
                    defaultValue: `Discard ${selectedQueries.size} selected queries?`,
                  })}
                </p>
                <ul
                  className="list-group"
                  style={{ maxHeight: 200, overflowY: "auto" }}
                >
                  {getSelectedItems().map((q) => (
                    <li
                      key={q.generated_query_id}
                      className="list-group-item py-2"
                    >
                      {q.query}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleBulkDiscard}
                  disabled={isBulkDeleting}
                >
                  {isBulkDeleting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {t("common.discarding", "Discarding...")}
                    </>
                  ) : (
                    t("common.discard", "Discard")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
